import { ModuleRequestError, UploadAbortedError, abortableSleep, fileSha256, untilAborted, uploadInParts, } from '@wit-support/admin-client-core';
import { abortUpload, completeUpload, initUpload, signUploadParts } from './api.js';
import { readVideoFacts } from './media-meta.js';
/** Что принимает медиатека (проверяет auto-ads; здесь — фильтр выбора файлов). */
export const ADS_MEDIA_ACCEPT = 'image/jpeg,image/png,video/mp4,video/quicktime,.jpg,.jpeg,.png,.mp4,.mov';
const BY_EXT = {
    jpg: { kind: 'image', contentType: 'image/jpeg' },
    jpeg: { kind: 'image', contentType: 'image/jpeg' },
    png: { kind: 'image', contentType: 'image/png' },
    mp4: { kind: 'video', contentType: 'video/mp4' },
    mov: { kind: 'video', contentType: 'video/quicktime' },
};
/**
 * Тип медиа по файлу: MIME браузера, а если он пуст (бывает у `.mov`) — по
 * расширению. null — файл не картинка и не видео.
 */
export function mediaKindOf(file) {
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('image/'))
        return { kind: 'image', contentType: type };
    if (type.startsWith('video/'))
        return { kind: 'video', contentType: type };
    const ext = file.name.toLowerCase().split('.').pop() ?? '';
    return BY_EXT[ext] ?? null;
}
/** Временный сбой запроса к admin-api: повтор того же запроса может пройти. */
export function isTransientRequestError(err) {
    if (!(err instanceof ModuleRequestError))
        return !(err instanceof UploadAbortedError);
    return err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500;
}
/**
 * Прошлый `complete` этой сессии ещё выполняется на сервере (auto-ads 409
 * `invalid-state` «upload is being completed»): он может закончиться успешно,
 * поэтому это не отказ — complete повторяется с паузой, а файл заново не
 * грузится (иначе — второй ассет, когда первый complete дойдёт).
 */
export function isCompleteBusy(err) {
    return err instanceof ModuleRequestError && err.upstreamCode === 'invalid-state';
}
/** Сбой завершения, после которого повторять стоит только `complete`. */
export function isRetryableCompleteError(err) {
    return isTransientRequestError(err) || isCompleteBusy(err);
}
/** Пока прошлый complete выполняется: пауза и число опросов (≈ 2 мин). */
export const COMPLETE_BUSY_DELAY_MS = 5_000;
export const COMPLETE_BUSY_POLLS = 24;
/**
 * Загрузить файл в медиатеку:
 * 1. sha256 файла (≤ 200 МБ, `fileSha256`) — тот же файл уже есть: сервер
 *    отвечает «уже есть», и ничего не грузится;
 *    параллельно у видео — разрешение и длительность из метаданных
 *    (`readVideoFacts`), они уходят в начало загрузки и сразу видны (v5 N7);
 * 2. начало загрузки → подписанные ссылки на части (16 МиБ);
 * 3. части — прямо в S3 (`uploadInParts`: параллельно, ретраи, свежие ссылки
 *    при истёкшей подписи, прогресс, отмена) → завершение по `ETag`.
 *
 * Кабинеты не выбираются: файл переносится в кабинет запуска автоматически.
 * Ошибки — `ModuleRequestError`; отмена — ошибка с `name === 'AbortError'`.
 */
export async function uploadAsset(file, opts = {}) {
    const media = mediaKindOf(file);
    if (!media) {
        throw new ModuleRequestError('unsupported_media_type', 415, {
            serverMessage: 'Этот тип файла не подходит: нужны JPEG, PNG, MP4 или MOV',
        });
    }
    const name = opts.name?.trim();
    const pause = opts.sleep ?? abortableSleep;
    // факты видео читаются параллельно с sha256 (только заголовок файла);
    // картинкам auto-ads их не принимает — размеры картинки определит сам
    const factsP = media.kind === 'video'
        ? (opts.readVideoFacts ?? readVideoFacts)(file).catch(() => ({}))
        : Promise.resolve({});
    void factsP.then((f) => {
        if (Object.keys(f).length > 0)
            opts.onFacts?.(f);
    });
    const [sha256, facts] = await Promise.all([(opts.sha256 ?? fileSha256)(file), factsP]);
    if (opts.signal?.aborted)
        throw new UploadAbortedError();
    const init = await initUpload({
        kind: media.kind,
        filename: file.name,
        contentType: media.contentType,
        byteLength: file.size,
        ...(name ? { name } : {}),
        ...(sha256 ? { sha256 } : {}),
        ...facts,
    });
    if (init.deduplicated)
        return { asset: init.asset, deduplicated: true };
    opts.onUploading?.();
    const asset = await uploadInParts({
        file,
        plan: init,
        refreshUrls: async (partNumbers) => (await signUploadParts(init.uploadId, partNumbers)).parts,
        complete: async (parts, signal) => {
            // опрос «complete ещё выполняется» прерывается отменой (паузой и проверкой)
            const finish = async (sig) => {
                for (let poll = 0;; poll += 1) {
                    if (sig?.aborted)
                        throw new UploadAbortedError();
                    try {
                        return (await untilAborted(completeUpload(init.uploadId, parts), sig)).asset;
                    }
                    catch (err) {
                        if (!isCompleteBusy(err) || poll >= COMPLETE_BUSY_POLLS)
                            throw err;
                        await pause(COMPLETE_BUSY_DELAY_MS, sig);
                    }
                }
            };
            // повтор только завершения (кнопка «Повторить»): своя отмена — сессия
            // закрывается на сервере, как при отмене в `uploadInParts`
            const retryFinish = async (sig) => {
                try {
                    return await finish(sig);
                }
                catch (err) {
                    if (sig?.aborted) {
                        await abortUpload(init.uploadId).catch(() => undefined);
                        throw new UploadAbortedError();
                    }
                    throw err;
                }
            };
            try {
                return await finish(signal);
            }
            catch (err) {
                if (!signal?.aborted && isRetryableCompleteError(err)) {
                    opts.onCompleteFailed?.(retryFinish);
                }
                throw err;
            }
        },
        abort: () => abortUpload(init.uploadId),
        ...(opts.onProgress ? { onProgress: opts.onProgress } : {}),
        ...(opts.signal ? { signal: opts.signal } : {}),
        ...(opts.xhrFactory ? { xhrFactory: opts.xhrFactory } : {}),
        ...(opts.sleep ? { sleep: opts.sleep } : {}),
    });
    return { asset, deduplicated: false };
}
//# sourceMappingURL=upload.js.map